import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { Team, TeamWithPlayers } from '@/types';
import { teamRepo } from '@/db/repositories/teamRepo';

interface TeamState {
  teams: TeamWithPlayers[];
  isLoading: boolean;
  error: string | null;
}

interface TeamActions {
  loadTeams: () => Promise<void>;
  addTeam: (name: string, player1Id: string, player2Id: string) => Promise<Team | null>;
  updateTeam: (
    id: string,
    updates: { name?: string; player1Id?: string; player2Id?: string }
  ) => Promise<boolean>;
  deleteTeam: (id: string) => Promise<boolean>;
  getTeamById: (id: string) => TeamWithPlayers | undefined;
  clearError: () => void;
}

type TeamStore = TeamState & TeamActions;

export const useTeamStore = create<TeamStore>()(
  persist(
    immer((set, get) => ({
      // State
      teams: [],
      isLoading: false,
      error: null,

      // Actions
      loadTeams: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const teams = await teamRepo.getAllWithPlayers();
          set((state) => {
            state.teams = teams;
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to load teams';
            state.isLoading = false;
          });
        }
      },

      addTeam: async (name: string, player1Id: string, player2Id: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          // Check for duplicate name
          const nameExists = await teamRepo.nameExists(name);
          if (nameExists) {
            set((state) => {
              state.error = 'A team with this name already exists';
            });
            return null;
          }

          // Check if team with same players exists
          const playersExist = await teamRepo.playersExist(player1Id, player2Id);
          if (playersExist) {
            set((state) => {
              state.error = 'A team with these players already exists';
            });
            return null;
          }

          const team = await teamRepo.create(name, player1Id, player2Id);

          // Reload to get team with players
          const teamWithPlayers = await teamRepo.getWithPlayers(team.id);
          if (teamWithPlayers) {
            set((state) => {
              state.teams.push(teamWithPlayers);
            });
          }

          return team;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to add team';
          });
          return null;
        }
      },

      updateTeam: async (
        id: string,
        updates: { name?: string; player1Id?: string; player2Id?: string }
      ) => {
        set((state) => {
          state.error = null;
        });

        try {
          // Check for duplicate name
          if (updates.name) {
            const nameExists = await teamRepo.nameExists(updates.name, id);
            if (nameExists) {
              set((state) => {
                state.error = 'A team with this name already exists';
              });
              return false;
            }
          }

          // Check if team with same players exists
          if (updates.player1Id || updates.player2Id) {
            const existing = await teamRepo.getById(id);
            if (existing) {
              const p1 = updates.player1Id ?? existing.player1Id;
              const p2 = updates.player2Id ?? existing.player2Id;
              const playersExist = await teamRepo.playersExist(p1, p2, id);
              if (playersExist) {
                set((state) => {
                  state.error = 'A team with these players already exists';
                });
                return false;
              }
            }
          }

          const updated = await teamRepo.update(id, updates);
          if (updated) {
            // Reload to get updated team with players
            const teamWithPlayers = await teamRepo.getWithPlayers(id);
            if (teamWithPlayers) {
              set((state) => {
                const index = state.teams.findIndex((t) => t.id === id);
                if (index !== -1) {
                  state.teams[index] = teamWithPlayers;
                }
              });
            }
            return true;
          }
          return false;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to update team';
          });
          return false;
        }
      },

      deleteTeam: async (id: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          const deleted = await teamRepo.delete(id);
          if (deleted) {
            set((state) => {
              state.teams = state.teams.filter((t) => t.id !== id);
            });
            return true;
          }
          return false;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to delete team';
          });
          return false;
        }
      },

      getTeamById: (id: string) => {
        return get().teams.find((t) => t.id === id);
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },
    })),
    {
      name: 'belot-teams',
      partialize: (state) => ({ teams: state.teams }),
    }
  )
);
